/** Drop lowest-priority dehydrated queries until persisted payload fits under cap. */

export const MAX_PERSIST_BYTES = 4 * 1024 * 1024;

export function estimatePersistBytes(value) {
  if (value == null) return 0;
  try {
    return new Blob([value]).size;
  } catch {
    return String(value).length * 2;
  }
}

/** Higher = dropped first when pruning. */
export function queryDropPriority(queryKey) {
  if (!Array.isArray(queryKey) || queryKey.length === 0) return 1000;
  const [root, sub] = queryKey;

  // Festival offline set - kept longest (operator, 2026-09-23: with no service at the festival, maps, set times and
  // Find must already be on the phone). Before this, festival-map fell to the default and was dropped FIRST.
  if (root === 'festival-map') return 8;
  if (root === 'events' && sub === 'lineup') return 10;
  if (root === 'sets') return 12;
  if (root === 'groups' && queryKey[2] === 'member-voters') return 12;

  if (root === 'events') {
    if (sub === 'detail') return 100;
    if (sub === 'search') return 90;
    if (sub === 'trending') return 80;
    if (sub === 'feed') return 45;
    if (sub === 'genres') return 30;
  }
  if (root === 'people') return 35;
  if (root === 'profiles') {
    if (sub === 'viewer-rsvps') return 60;
    if (queryKey[2] === 'events') return 50;
    return 40;
  }
  if (root === 'rsvps') return 20;
  if (root === 'find') return 18;
  if (root === 'chats') {
    if (sub === 'pending') return 15;
    if (sub === 'inbox') return 5;
    if (sub === 'dm' && queryKey[3] === 'messages') return 85;
    if (sub === 'group' && queryKey[2] === 'messages') return 85;
    if (sub === 'dm' || sub === 'group') return 75;
  }
  if (root === 'groups' && queryKey[2] === 'messages') return 85;
  if (root === 'groups') return 75;
  return 500;
}

export function prunePersistedPayload(serialized, maxBytes = MAX_PERSIST_BYTES) {
  if (!serialized || estimatePersistBytes(serialized) <= maxBytes) return serialized;

  let parsed;
  try {
    parsed = JSON.parse(serialized);
  } catch {
    return serialized;
  }

  const queries = parsed?.clientState?.queries;
  if (!Array.isArray(queries) || queries.length === 0) return serialized;

  let currentQueries = [...queries];

  while (currentQueries.length > 0) {
    const candidate = JSON.stringify({
      ...parsed,
      clientState: {
        ...parsed.clientState,
        queries: currentQueries,
        mutations: parsed.clientState?.mutations,
      },
    });
    if (estimatePersistBytes(candidate) <= maxBytes) return candidate;

    let worstIdx = 0;
    let worstPriority = -Infinity;
    currentQueries.forEach((q, i) => {
      const priority = queryDropPriority(q.queryKey);
      if (priority > worstPriority) {
        worstPriority = priority;
        worstIdx = i;
      }
    });
    currentQueries.splice(worstIdx, 1);
  }

  return serialized;
}
